import axiosInstanceApiGateway from 'config/apiServiceApiGateway'

const { Post } = axiosInstanceApiGateway

type RequestOrderAutoAssignType = {
  order_id: number
  location_id: number
}

export const postAutoAssignOrder = (payload: RequestOrderAutoAssignType) =>
  Post({ url: '/logistic/internal/v1/admin/auto-assign', data: payload })

export const putUnassignDriver = (orderId: number) =>
  axiosInstanceApiGateway.Put({ url: `/logistic/internal/v1/admin/unassignment/${orderId}` })
// Timestamp in the error message is assumed to be the previous queue start time.
// Keep this parser isolated until the backend error format is finalized.
export const parseAutoAssignTimestamp = (message: string) => {
  const timestamp = message.match(
    /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})/i,
  )?.[0]
  const date = timestamp ? new Date(timestamp).getTime() : NaN
  return Number.isFinite(date) ? date : null
}

type AutoAssignTimerType = { calledAt: number; expiresAt: number }
const timerPrefix = 'auto-assign:v1:'
const memoryTimers = new Map<string, AutoAssignTimerType>()
export const autoAssignTimerKey = (locationId: string, userId: string | number, orderId: number) =>
  `${timerPrefix}${locationId}:${userId}:${orderId}`

export const readAutoAssignTimer = (key: string): AutoAssignTimerType | null => {
  if (memoryTimers.has(key)) return memoryTimers.get(key) || null
  try {
    const raw = localStorage.getItem(key)
    const value = raw ? JSON.parse(raw) : null
    if (value && Number.isFinite(value.calledAt) && Number.isFinite(value.expiresAt)) return value
    return memoryTimers.get(key) || null
  } catch {
    return memoryTimers.get(key) || null
  }
}

export const removeAutoAssignTimer = (key: string) => {
  memoryTimers.delete(key)
  try {
    localStorage.removeItem(key)
  } catch {
    /* Storage can be unavailable. */
  }
}

export const saveAutoAssignTimer = (key: string, calledAt = Date.now()) => {
  const value = { calledAt, expiresAt: calledAt + 30_000 }
  if (value.expiresAt <= Date.now()) {
    removeAutoAssignTimer(key)
    return
  }
  try {
    localStorage.setItem(key, JSON.stringify(value))
    memoryTimers.delete(key)
  } catch {
    memoryTimers.set(key, value)
  }
}

export const reconcileAutoAssignTimers = (
  locationId: string,
  orders: { order_id: number; staff: { driver: { id: string } } }[],
  requestedAt: number,
) => {
  const keys = new Set(memoryTimers.keys())
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i)
      if (key) keys.add(key)
    }
  } catch {
    /* Use memory fallback. */
  }
  keys.forEach((key) => {
    if (!key.startsWith(`${timerPrefix}${locationId}:`)) return
    const timer = readAutoAssignTimer(key)
    const orderId = Number(key.split(':').pop())
    if (
      timer &&
      (timer.expiresAt <= Date.now() ||
        (timer.calledAt <= requestedAt &&
          orders.some(
            (order) =>
              order.order_id === orderId &&
              Boolean(order.staff?.driver?.id) &&
              order.staff.driver.id !== '0',
          )))
    ) {
      removeAutoAssignTimer(key)
    }
  })
}
